import config from '../../config';
import webSerialPrinter from './WebSerialPrinter';
import browserPrinter from './BrowserPrinter';
import { buildInvoiceTicket, buildTestTicket, buildTicketPreview, buildTicket } from './TsplTicketBuilder';

export const PRINTER_TRANSPORTS = {
    TSC: 'web-serial',
    SYSTEM: 'system-dialog'
};

const CONFIG_VERSION = 4;
const DEFAULT_CONFIGURATION = {
    version: CONFIG_VERSION,
    model: 'TSC Alpha-3R/3RB',
    transport: 'web-serial',
    paperWidthMm: 80,
    printableWidthDots: 576,
    baudRate: 9600,
    autoPrint: true,
    portFingerprint: null,
    printerName: null
};

const getUserIdentifier = (user = {}) =>
    user.ID || user.id || user.UserID || user.USERID || user.U_ID || user.U_NAME || 'anonymous';

const configurationKey = (user) =>
    `${config.localStorageNamed}printerConfiguration:${getUserIdentifier(user)}`;

const lastTicketKey = (user) =>
    `${config.localStorageNamed}lastTsplTicket:${getUserIdentifier(user)}`;

const compactTicketDocument = (document) => {
    const docHeader = document.docHeader || {};
    const header = docHeader.responseHeader || {};
    const info = docHeader.infoByFolio || {};
    const headerFields = [
        'U_BPP_MDTD', 'U_BPP_MDSD', 'U_BPP_MDCD', 'U_BPP_BPTD', 'U_BPP_BPTDName',
        'U_BPP_TtPc', 'U_FMB_User', 'U_STR_CDG_HASH', 'QRValue', 'DocNum', 'NumAtCard',
        'DocDate', 'DocDueDate', 'CreateTS', 'DocTime', 'PymntGroup', 'GroupName', 'PaymentCondition',
        'CondPago', 'DocCur', 'CurrencyName', 'CardName', 'LicTradNum', 'SlpName',
        'DocTotal', 'VatSum', 'DiscSum', 'DiscSumSy', 'PerceptionTotal'
    ];
    const infoFields = ['folio', 'DocDate'];
    const lineFields = [
        'ItemCode', 'Dscription', 'ItemName', 'itemName', 'Quantity', 'quantity',
        'PriceAfVAT', 'Price', 'price', 'LineTotal', 'SalUnitMsr', 'MeasureUnit', 'UM',
        'TaxCode', 'taxCode', 'VatGroup', 'U_BPP_OPER', 'uBppOper', 'OperationType',
        'DiscPrcnt', 'discPrcnt'
    ];
    const pick = (source, fields) => fields.reduce((result, field) => {
        if (source[field] !== undefined && source[field] !== null) result[field] = source[field];
        return result;
    }, {});

    return {
        docHeader: {
            responseHeader: pick(header, headerFields),
            infoByFolio: pick(info, infoFields),
            payMethods: Array.isArray(docHeader.payMethods)
                ? docHeader.payMethods.map((payment) => pick(payment, ['pm_Name', 'amount']))
                : []
        },
        docLines: Array.isArray(document.docLines)
            ? document.docLines.map((line) => pick(line, lineFields))
            : []
    };
};

class PrinterService {
    constructor() {
        this.connectionPromise = null;
        this.activeUser = null;
        this.connectionListeners = new Set();
        webSerialPrinter.subscribeConnection(() => this.notifyConnectionChange());
    }

    getConfiguration(user) {
        try {
            const saved = JSON.parse(localStorage.getItem(configurationKey(user)));
            const configuration = { ...DEFAULT_CONFIGURATION, ...(saved || {}) };
            if (configuration.transport === PRINTER_TRANSPORTS.SYSTEM || configuration.transport === 'windows') {
                return {
                    ...configuration,
                    transport: PRINTER_TRANSPORTS.SYSTEM,
                    model: 'Impresoras del sistema',
                    printerName: null,
                    portFingerprint: null
                };
            }
            return {
                ...configuration,
                transport: PRINTER_TRANSPORTS.TSC,
                model: 'TSC Alpha-3R/3RB',
                printerName: null
            };
        } catch (error) {
            return { ...DEFAULT_CONFIGURATION };
        }
    }

    saveConfiguration(user, configuration) {
        const transport = Object.values(PRINTER_TRANSPORTS).includes(configuration.transport)
            ? configuration.transport
            : PRINTER_TRANSPORTS.TSC;
        const value = {
            ...DEFAULT_CONFIGURATION,
            ...configuration,
            transport,
            model: transport === PRINTER_TRANSPORTS.TSC
                ? 'TSC Alpha-3R/3RB'
                : 'Impresoras del sistema',
            printerName: null,
            paperWidthMm: 80,
            printableWidthDots: 576,
            baudRate: 9600,
            version: CONFIG_VERSION,
            updatedAt: new Date().toISOString()
        };
        localStorage.setItem(configurationKey(user), JSON.stringify(value));
        this.activeUser = user;
        this.notifyConnectionChange();
        return value;
    }

    isSystemPrinter(configuration) {
        return configuration.transport === PRINTER_TRANSPORTS.SYSTEM;
    }

    isSupported(configuration = null) {
        if (configuration && this.isSystemPrinter(configuration)) return browserPrinter.isSupported();
        return webSerialPrinter.isSupported();
    }

    isConnected(user = this.activeUser) {
        const configuration = this.getConfiguration(user || {});
        if (this.isSystemPrinter(configuration)) return browserPrinter.isSupported();
        return webSerialPrinter.isConnected();
    }

    notifyConnectionChange() {
        const connected = this.isConnected();
        this.connectionListeners.forEach((listener) => listener(connected));
    }

    subscribeConnection(listener) {
        if (typeof listener !== 'function') return () => { };
        this.connectionListeners.add(listener);
        listener(this.isConnected());
        return () => this.connectionListeners.delete(listener);
    }

    async connectConfiguredPrinter(user) {
        this.activeUser = user;
        const configuration = this.getConfiguration(user);
        if (this.isSystemPrinter(configuration)) return browserPrinter.isSupported();
        if (!webSerialPrinter.isSupported()) return false;
        if (webSerialPrinter.isConnected()) return true;
        if (this.connectionPromise) return this.connectionPromise;
        if (!configuration.portFingerprint) return false;

        this.connectionPromise = (async () => {
            try {
                await webSerialPrinter.open(configuration, false);
                return webSerialPrinter.isConnected();
            } catch (error) {
                return false;
            } finally {
                this.connectionPromise = null;
            }
        })();

        return this.connectionPromise;
    }

    async selectPrinter(user, configuration = {}) {
        const currentConfiguration = { ...this.getConfiguration(user), ...configuration };
        const portFingerprint = await webSerialPrinter.selectPort();
        const selectedConfiguration = {
            ...currentConfiguration,
            portFingerprint
        };

        // requestPort solamente concede permiso. Abrir el puerto mantiene activa
        // la conexión Bluetooth y confirma que la impresora realmente responde.
        await webSerialPrinter.open(selectedConfiguration);
        return this.saveConfiguration(user, selectedConfiguration);
    }

    async write(rawData, configuration, allowSelection = false) {
        if (this.isSystemPrinter(configuration)) {
            return browserPrinter.print(rawData);
        }
        return webSerialPrinter.write(rawData, configuration, allowSelection);
    }

    async testPrinter(user, configuration = {}, allowSelection = false, printFullPreview = false) {
        const currentConfiguration = this.saveConfiguration(user, {
            ...this.getConfiguration(user),
            ...configuration
        });
        const tspl = printFullPreview
            ? buildTicketPreview(currentConfiguration)
            : buildTestTicket(currentConfiguration);
        await this.write(tspl, currentConfiguration, allowSelection);
        return currentConfiguration;
    }

    rememberLastTicket(user, document) {
        if (!document) return;
        try {
            localStorage.setItem(lastTicketKey(user), JSON.stringify({
                savedAt: new Date().toISOString(),
                document: compactTicketDocument(document)
            }));
        } catch (error) {
            // El documento sigue disponible en la vista aunque el almacenamiento local este lleno.
        }
    }

    getLastTicket(user) {
        try {
            const saved = JSON.parse(localStorage.getItem(lastTicketKey(user)));
            return saved && saved.document ? saved : null;
        } catch (error) {
            return null;
        }
    }

    async printInvoice(user, document, options = {}) {
        const configuration = this.getConfiguration(user);
        // if (options.automatic && !configuration.autoPrint) {
        //     return { skipped: true, reason: 'AUTO_PRINT_DISABLED' };
        // }
        const tspl = buildInvoiceTicket(document, user, configuration);
        await this.write(tspl, configuration, Boolean(options.allowSelection));
        if (options.remember !== false) {
            this.rememberLastTicket(user, document);
        }
        return { skipped: false };
    }

    async printInvoices(user, documents = [], options = {}) {
        const printableDocuments = Array.isArray(documents) ? documents.filter(Boolean) : [];
        if (printableDocuments.length === 0) {
            return { skipped: true, reason: 'NO_DOCUMENTS', count: 0 };
        }

        const configuration = this.getConfiguration(user);
        const tsplBatch = printableDocuments
            .map((document) => buildInvoiceTicket(document, user, configuration))
            .join('\r\n');

        await this.write(tsplBatch, configuration, Boolean(options.allowSelection));

        if (options.remember !== false) {
            this.rememberLastTicket(user, printableDocuments[printableDocuments.length - 1]);
        }

        return { skipped: false, count: printableDocuments.length };
    }

    async printTickets(user, documents = [], options = {}) {
        const printableDocuments = Array.isArray(documents) ? documents.filter(Boolean) : [];
        if (printableDocuments.length === 0) {
            return { skipped: true, reason: 'NO_DOCUMENTS', count: 0 };
        }

        const configuration = this.getConfiguration(user);

        const tsplBatch = printableDocuments
            .map((document) => buildTicket(document, user, configuration))
            .join('\r\n');

        await this.write(tsplBatch, configuration, Boolean(options.allowSelection));

        if (options.remember !== false) {
            this.rememberLastTicket(user, printableDocuments[printableDocuments.length - 1]);
        }

        return { skipped: false, count: printableDocuments.length };
    }

    async printLastTicket(user) {
        const lastTicket = this.getLastTicket(user);
        if (!lastTicket) {
            throw new Error('Todavia no hay un ticket guardado en este equipo.');
        }
        await this.printInvoice(user, lastTicket.document);
        return lastTicket;
    }

    async disconnect() {
        await webSerialPrinter.disconnect();
    }
}

export default new PrinterService();
